import React, { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { GameCanvas } from './components/GameCanvas'
import { GlobalTopbar } from './components/GlobalTopbar'
import { MatchBar } from './components/MatchBar'
import { MatchTutorial } from './components/MatchTutorial'
import { TowerBar } from './components/TowerBar'
import { GlobalFooter } from './components/GlobalFooter'
import { HomeMenu } from './components/HomeMenu'
import { ResearchLab } from './components/ResearchLab'
import { GameMenu } from './components/GameMenu'
import { MapEditorPage } from './editor/MapEditorPage'
import { DevLabsPage } from './components/DevLabsPage'
import { DevVisualsPage } from './components/DevVisualsPage'
import { formatSessionTime } from './game/model/session'
import { formatCompactNumber } from './game/model/formatNumber'
import { restartSession } from './game/model/sessionHost'
import { useMatchTutorial } from './onboarding/useMatchTutorial'
import { useShellLayoutMode } from './shell/useShellLayoutMode'
import { useGameStore } from './store/gameStore'
import { useCampaignProgressRecorder } from './progression/useCampaignProgressRecorder'
import { formatBestTime, campaignProgress, chapterStarTotal } from './progression/campaignProgress'
import { getChapter, getChapterForStage, nextStageId } from './game/campaign'
import { productStageName } from './game/campaign/playerTerms'

function GameShell() {
  const navigate = useNavigate()
  const { gameState, isPaused, sessionView, playtestMap, endPlaytest, startStage, resetGame } =
    useGameStore()
  const tutorial = useMatchTutorial()
  const shellMode = useShellLayoutMode()
  const { lastVictoryMeta } = useCampaignProgressRecorder()
  const inMatch = gameState === 'playing'
  const matchOver =
    sessionView?.state === 'DEFEAT' || sessionView?.state === 'VICTORY'
  const showHome = gameState === 'menu' || gameState === 'gameOver' || gameState === 'victory'
  const isPlaytest = playtestMap !== null
  const showFooter = !inMatch
  const landscapeMatch = inMatch && !matchOver && shellMode === 'landscape'
  const desktopMatch = inMatch && !matchOver && shellMode === 'desktop'
  const portraitMatch = inMatch && !matchOver && shellMode === 'portrait'
  const campaign = sessionView?.campaign ?? null
  const upcomingStageId = campaign ? nextStageId(campaign.stageId) : null
  const campaignChapter = campaign
    ? getChapter(campaign.chapterId) ?? getChapterForStage(campaign.stageId)
    : null
  const chapterProgressStars = campaignChapter
    ? chapterStarTotal(campaignProgress.load(), campaignChapter.id)
    : null

  useEffect(() => {
    const highlight = tutorial.enabled ? tutorial.view.highlight : null
    if (highlight) {
      document.body.dataset.tutorialHighlight = highlight
    } else {
      delete document.body.dataset.tutorialHighlight
    }
    return () => {
      delete document.body.dataset.tutorialHighlight
    }
  }, [tutorial.enabled, tutorial.view.highlight])

  useEffect(() => {
    document.body.dataset.shellLayout = shellMode
    return () => {
      delete document.body.dataset.shellLayout
    }
  }, [shellMode])

  const exitPlaytest = () => {
    endPlaytest()
    navigate('/map-editor')
  }

  const matchStage = (
    <>
      {(landscapeMatch || desktopMatch) && <MatchBar />}
      <GameCanvas />
      {tutorial.enabled && !matchOver && (
        <MatchTutorial
          view={tutorial.view}
          onSkip={tutorial.skip}
          onDismiss={tutorial.dismissInfo}
          onAckEnemy={tutorial.acknowledgeEnemy}
          onAckMechanic={tutorial.acknowledgeMechanic}
        />
      )}
    </>
  )

  return (
    <div
      className={[
        'app-shell',
        inMatch ? 'app-shell--match' : '',
        `app-shell--${shellMode}`
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <GlobalTopbar />

      {portraitMatch && <MatchBar />}

      <main className="app-shell__main">
        {showHome && !matchOver && (
          <HomeMenu onReplayTutorial={tutorial.resetForReplay} />
        )}
        {inMatch && (
          <div className="match-workspace">
            <div className="match-workspace__stage">{matchStage}</div>
            {!matchOver && (
              <aside className="match-workspace__rail" aria-label="Tower controls">
                <TowerBar />
              </aside>
            )}
          </div>
        )}
      </main>

      {showFooter && <GlobalFooter />}

      {isPaused && inMatch && !matchOver && (
        <div className="shell-overlay">
          <GameMenu />
        </div>
      )}

      {sessionView?.state === 'DEFEAT' && (
        <div className="shell-overlay" role="dialog" aria-labelledby="defeat-title">
          <div className="defeat-panel defeat-panel--compact">
            <h2 id="defeat-title" className="defeat-panel__title">
              DEFEAT
            </h2>
            {campaign && (
              <p className="defeat-panel__meta" data-testid="defeat-stage">
                Level {campaign.displayLabel} · {String(campaign.difficultyId).toUpperCase()}
              </p>
            )}
            <p className="defeat-panel__meta">
              Wave {sessionView.waveNumber}/{sessionView.waveCount}
            </p>
            <p className="defeat-panel__meta">
              Defeated {sessionView.defeatedEnemies} · {formatSessionTime(sessionView.elapsedTime)}
            </p>
            <p className="defeat-panel__meta">
              Damage dealt {formatCompactNumber(sessionView.totalDamageDealt)}
            </p>
            <div className="defeat-panel__actions">
              {isPlaytest ? (
                <button type="button" className="btn-primary defeat-panel__restart" onClick={exitPlaytest}>
                  EXIT PLAYTEST
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn-primary defeat-panel__restart"
                    data-testid="defeat-retry"
                    onPointerUp={(event) => {
                      event.preventDefault()
                      restartSession()
                    }}
                  >
                    RETRY
                  </button>
                  <button
                    type="button"
                    className="btn-secondary defeat-panel__restart"
                    data-testid="defeat-home"
                    onPointerUp={(event) => {
                      event.preventDefault()
                      resetGame()
                    }}
                  >
                    HOME
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {sessionView?.state === 'VICTORY' && (
        <div className="shell-overlay" role="dialog" aria-labelledby="victory-title">
          <div className="defeat-panel defeat-panel--victory defeat-panel--compact">
            <h2 id="victory-title" className="defeat-panel__title defeat-panel__title--victory">
              VICTORY
            </h2>
            {campaign ? (
              <p className="defeat-panel__campaign" data-testid="stage-clear">
                LEVEL {campaign.displayLabel} CLEAR · {String(campaign.difficultyId).toUpperCase()}
              </p>
            ) : (
              !isPlaytest && (
                <p className="defeat-panel__campaign" data-testid="campaign-complete">
                  CAMPAIGN COMPLETE
                </p>
              )
            )}
            {lastVictoryMeta && lastVictoryMeta.newStars > 0 && (
              <p className="defeat-panel__meta" data-testid="victory-new-stars">
                New stars +{lastVictoryMeta.newStars} · {lastVictoryMeta.starCount}/4
              </p>
            )}
            {lastVictoryMeta && lastVictoryMeta.ingotsGranted > 0 && (
              <p className="defeat-panel__meta" data-testid="victory-ingots">
                First-clear Ingots +{lastVictoryMeta.ingotsGranted}
                {lastVictoryMeta.newlyClearedDifficulties.length > 1
                  ? ` (${lastVictoryMeta.newlyClearedDifficulties.join(', ')})`
                  : ''}
              </p>
            )}
            {lastVictoryMeta && lastVictoryMeta.chapterIngotsGranted > 0 && (
              <p className="defeat-panel__meta" data-testid="victory-chapter-ingots">
                {productStageName(campaignChapter)} complete · +
                {lastVictoryMeta.chapterIngotsGranted} Ingots
              </p>
            )}
            {chapterProgressStars && (
              <p className="defeat-panel__meta" data-testid="victory-chapter-progress">
                {productStageName(campaignChapter)} {chapterProgressStars.earned} /{' '}
                {chapterProgressStars.max} ★
              </p>
            )}
            <p className="defeat-panel__meta">
              Waves completed: {sessionView.wavesCompleted}/{sessionView.waveCount}
            </p>
            <p className="defeat-panel__meta">
              Defeated {sessionView.defeatedEnemies} · {formatSessionTime(sessionView.elapsedTime)}
            </p>
            <p className="defeat-panel__meta">
              Damage dealt {formatCompactNumber(sessionView.totalDamageDealt)}
            </p>
            {!isPlaytest && lastVictoryMeta?.entry && (
              <p className="defeat-panel__meta defeat-panel__meta--bests" data-testid="victory-bests">
                Best lives{' '}
                {lastVictoryMeta.entry.bestByDifficulty[
                  campaign?.difficultyId as import('./game/combat/types/primitives').DifficultyId
                ]?.bestLives ?? '—'}
                {lastVictoryMeta.newBestLives ? ' · New best' : ''}
                {' · '}
                Best time{' '}
                {formatBestTime(
                  lastVictoryMeta.entry.bestByDifficulty[
                    campaign?.difficultyId as import('./game/combat/types/primitives').DifficultyId
                  ]?.bestTime
                )}
                {lastVictoryMeta.newBestTime ? ' · New best' : ''}
              </p>
            )}
            <div className="defeat-panel__actions">
              {isPlaytest ? (
                <button type="button" className="btn-primary defeat-panel__restart" onClick={exitPlaytest}>
                  EXIT PLAYTEST
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn-primary defeat-panel__restart"
                    data-testid="victory-next"
                    disabled={
                      !upcomingStageId ||
                      !campaign ||
                      !(lastVictoryMeta?.nextStageUnlocked ?? false)
                    }
                    onPointerUp={(event) => {
                      event.preventDefault()
                      if (!upcomingStageId || !campaign) return
                      if (!(lastVictoryMeta?.nextStageUnlocked ?? false)) return
                      startStage(
                        upcomingStageId,
                        campaign.difficultyId as import('./game/combat/types/primitives').DifficultyId
                      )
                      restartSession()
                    }}
                  >
                    NEXT LEVEL
                  </button>
                  <button
                    type="button"
                    className="btn-secondary defeat-panel__restart"
                    data-testid="victory-replay"
                    onPointerUp={(event) => {
                      event.preventDefault()
                      restartSession()
                    }}
                  >
                    REPLAY
                  </button>
                  <button
                    type="button"
                    className="btn-secondary defeat-panel__restart"
                    data-testid="victory-home"
                    onPointerUp={(event) => {
                      event.preventDefault()
                      resetGame()
                    }}
                  >
                    HOME
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<GameShell />} />
        <Route
          path="/research"
          element={
            <div className="app-shell app-shell--research">
              <GlobalTopbar />
              <main className="app-shell__main app-shell__main--research">
                <ResearchLab />
              </main>
            </div>
          }
        />
        <Route
          path="/map-editor"
          element={
            <div className="app-shell app-shell--editor">
              <GlobalTopbar />
              <main className="app-shell__main app-shell__main--editor">
                <MapEditorPage />
              </main>
              <GlobalFooter />
            </div>
          }
        />
        <Route
          path="/dev/labs"
          element={
            <div className="app-shell app-shell--dev">
              <GlobalTopbar />
              <main className="app-shell__main app-shell__main--dev">
                <DevLabsPage />
              </main>
            </div>
          }
        />
        <Route
          path="/dev/visuals"
          element={
            <div className="app-shell app-shell--dev">
              <GlobalTopbar />
              <main className="app-shell__main app-shell__main--dev">
                <DevVisualsPage />
              </main>
            </div>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
